import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatListBulleted from "@mui/icons-material/FormatListBulleted";
import FormatListNumbered from "@mui/icons-material/FormatListNumbered";
import FormatTextdirectionLToROutlinedIcon from "@mui/icons-material/FormatTextdirectionLToROutlined";
import FormatTextdirectionRToLOutlinedIcon from "@mui/icons-material/FormatTextdirectionRToLOutlined";
import { Paper, Popper } from "@mui/material";
import type { Instance as PopperInstance } from "@popperjs/core";
import { useEditorState } from "@tiptap/react";
import {
  MenuButton,
  MenuButtonBold,
  MenuButtonEditLink,
  MenuButtonItalic,
  MenuButtonRemoveFormatting,
  MenuButtonUnderline,
  MenuControlsContainer,
  MenuSelectHeading,
  MenuSelectTextAlign,
  useRichTextEditorContext
} from "mui-tiptap";

const toolbarSx = {
  flexWrap: "nowrap" as const,
  ".MuiSelect-select": {
    padding: "2px 20px 2px 6px !important",
    color: "#071f35"
  },
  "& .MuiButtonBase-root": {
    color: "#071f35"
  },
  "& .MuiButtonBase-root svg": {
    color: "#071f35",
    fontSize: "18px"
  }
};

const floatingToolbarSx = {
  display: "flex",
  alignItems: "center",
  p: "2px 4px",
  borderRadius: "8px",
  border: "1px solid #E5E7EB",
  bgcolor: "#fff",
  boxShadow: "0 4px 16px rgba(7, 31, 53, 0.12)"
};

const btnSx = { fill: "#071f35" };

const InlineToolbar: React.FC = () => {
  const editor = useRichTextEditorContext();

  useEditorState({
    editor,
    selector: (snapshot) => snapshot.transactionNumber
  });

  return (
    <MenuControlsContainer sx={toolbarSx}>
      <MenuButtonBold sx={btnSx} />
      <MenuButtonItalic sx={btnSx} />
      <MenuButtonUnderline sx={btnSx} />
      <MenuSelectHeading sx={btnSx} />
      <MenuSelectTextAlign
        sx={{
          ".MuiSvgIcon-root": {
            fill: "#071f35 !important",
            fontSize: "18px"
          }
        }}
        emptyLabel={
          <FormatAlignLeftIcon
            sx={{ width: "16px", height: "16px", fill: "#071f35", verticalAlign: "middle", paddingTop: "2.5px" }}
          />
        }
      />
      <MenuButton
        tooltipLabel="Ordered list"
        tooltipShortcutKeys={["mod", "Shift", "7"]}
        IconComponent={FormatListNumbered}
        selected={editor?.isActive("orderedList") ?? false}
        disabled={!editor?.isEditable}
        onClick={() => editor?.chain().focus().toggleOrderedList().run()}
        sx={btnSx}
      />
      <MenuButton
        tooltipLabel="Bulleted list"
        tooltipShortcutKeys={["mod", "Shift", "8"]}
        IconComponent={FormatListBulleted}
        selected={editor?.isActive("bulletList") ?? false}
        disabled={!editor?.isEditable}
        onClick={() => editor?.chain().focus().toggleBulletList().run()}
        sx={btnSx}
      />
      <MenuButtonEditLink />
      <MenuButton
        tooltipLabel="Left to right"
        IconComponent={FormatTextdirectionLToROutlinedIcon}
        selected={editor?.isActive({ dir: "ltr" }) ?? false}
        disabled={!editor?.isEditable}
        onClick={() => editor?.chain().focus().setTextDirection("ltr").run()}
        sx={btnSx}
      />
      <MenuButton
        tooltipLabel="Right to left"
        IconComponent={FormatTextdirectionRToLOutlinedIcon}
        selected={editor?.isEditable ? (editor?.isActive({ dir: "rtl" }) ?? false) : false}
        disabled={!editor?.isEditable}
        onClick={() => editor?.chain().focus().setTextDirection("rtl").run()}
        sx={btnSx}
      />
      <MenuButtonRemoveFormatting sx={btnSx} />
    </MenuControlsContainer>
  );
};

/**
 * Floating formatting toolbar rendered as a popup outside the editor flow.
 * It appears only once the user has selected some text, and hides when the
 * selection collapses.
 *
 * Must be rendered inside a `RichTextEditorProvider`.
 */
export const FloatingToolbar: React.FC = () => {
  const editor = useRichTextEditorContext();
  const [open, setOpen] = useState(false);
  const openRef = useRef(false);
  const popperRef = useRef<PopperInstance | null>(null);
  const paperRef = useRef<HTMLDivElement | null>(null);

  // The Heading/Align dropdowns are MUI Selects: opening one portals its menu
  // OUTSIDE the editor and moves focus there, so the editor blurs. That blur
  // must NOT be treated as "clicked away" — otherwise the toolbar (and the open
  // menu inside it) unmounts before the option can be picked. Keep it alive while
  // focus is inside the toolbar Paper or any portaled menu/listbox it spawned.
  const isInteractingWithToolbar = useCallback(() => {
    const active = document.activeElement;
    if (!(active instanceof Element)) return false;
    if (paperRef.current?.contains(active)) return true;
    return !!active.closest(".MuiPopover-root, .MuiMenu-root, [role='listbox'], [role='menu'], [role='option']");
  }, []);
  // Position frozen at the caret. It is captured when the toolbar opens and is
  // intentionally NOT recomputed while a selection is being made, so the popup
  // stays put where the selection started.
  const anchorRectRef = useRef<DOMRect>(new DOMRect());

  const sel = useEditorState({
    editor,
    selector: (snapshot) => {
      const editorInstance = snapshot.editor;
      if (!editorInstance) return { from: 0, to: 0, empty: true, tn: snapshot.transactionNumber };
      const { from, to, empty } = editorInstance.state.selection;
      return { from, to, empty, tn: snapshot.transactionNumber };
    }
  }) ?? { from: 0, to: 0, empty: true, tn: 0 };

  // Virtual anchor that reports the frozen caret rectangle.
  const anchorEl = useMemo(() => ({ getBoundingClientRect: () => anchorRectRef.current }), []);

  // Stable so the Popper isn't recreated on every transaction (which would
  // momentarily detach button tooltips and flash them in the top-left corner).
  const popperModifiers = useMemo(
    () => [
      { name: "offset", options: { offset: [0, 8] } },
      { name: "preventOverflow", options: { padding: 8 } },
      { name: "flip", enabled: true }
    ],
    []
  );

  const show = useCallback(
    (caretPos: number) => {
      if (!editor) return;
      try {
        const c = editor.view.coordsAtPos(caretPos);
        anchorRectRef.current = new DOMRect(c.left, c.top, 1, c.bottom - c.top);
      } catch {
        anchorRectRef.current = new DOMRect();
      }
      openRef.current = true;
      setOpen(true);
      popperRef.current?.update();
    },
    [editor]
  );

  const hide = useCallback(() => {
    openRef.current = false;
    setOpen(false);
  }, []);

  // Show the toolbar only while there is a (non-empty) text selection — keeping it
  // open when focus is in the toolbar's own dropdown menus rather than the editor.
  useEffect(() => {
    if (!editor) return;
    if (!sel.empty && (editor.isFocused || isInteractingWithToolbar())) {
      // Open at the frozen position; if already open, leave it where it is
      // (no recalculation as the selection grows).
      if (!openRef.current) show(sel.from);
    } else {
      hide();
    }
  }, [editor, sel.empty, sel.from, sel.to, sel.tn, show, hide, isInteractingWithToolbar]);

  if (!editor) return null;

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="top-start"
      popperRef={popperRef}
      modifiers={popperModifiers}
      sx={{ zIndex: 1300 }}
    >
      <Paper
        ref={paperRef}
        sx={floatingToolbarSx}
        // Keep the editor focused & the selection intact when interacting with
        // the toolbar, otherwise formatting commands run against a lost selection.
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <InlineToolbar />
      </Paper>
    </Popper>
  );
};
